import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * Fast, dependency-free hover tooltip for dense table cells.
 *
 * Deliberately NOT Radix `Tooltip`: a recap product cell needs TWO of these
 * (SKU + product name) and the cell renders once per flow row, so a Radix
 * root per trigger would multiply the mounted-provider count for content that
 * is idle 99% of the time. Here the idle cost is one `useState` + a few
 * handlers; the panel only exists in the DOM while it is actually shown.
 *
 * Default delay is short on purpose — these tooltips exist to read a value the
 * column is too narrow to print, so the answer has to arrive at scan speed.
 */
const DEFAULT_DELAY_MS = 120;
const MAX_WIDTH_PX = 360;
const GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;
/** Space needed below the anchor before we prefer the below placement. */
const FLIP_THRESHOLD_PX = 120;

interface TooltipAnchor {
  left: number;
  top?: number;
  bottom?: number;
}

export interface HoverTooltipTriggerProps {
  onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerLeave: () => void;
  onPointerDown: () => void;
}

export interface HoverTooltipResult {
  triggerProps: HoverTooltipTriggerProps;
  /** Render this next to the trigger — it portals to <body>. */
  tooltip: ReactNode;
}

export function useHoverTooltip(
  content: ReactNode,
  delayMs: number = DEFAULT_DELAY_MS,
): HoverTooltipResult {
  const [anchor, setAnchor] = useState<TooltipAnchor | null>(null);
  const timerRef = useRef<number | null>(null);
  // Mirrors `anchor` for the pointermove guard — reading state there would
  // make the handler identity churn on every open/close.
  const isOpenRef = useRef(false);

  const cancelPending = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelPending();
    isOpenRef.current = false;
    setAnchor(null);
  }, [cancelPending]);

  const scheduleOpen = useCallback(
    (element: HTMLElement) => {
      if (isOpenRef.current || timerRef.current !== null) return;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        const rect = element.getBoundingClientRect();
        const left = Math.max(
          VIEWPORT_MARGIN_PX,
          Math.min(
            rect.left,
            window.innerWidth - MAX_WIDTH_PX - VIEWPORT_MARGIN_PX,
          ),
        );
        // Flip above when the bottom of the viewport is close. Anchoring the
        // flipped panel by `bottom` avoids having to measure its height.
        const fitsBelow =
          window.innerHeight - rect.bottom > FLIP_THRESHOLD_PX ||
          rect.top < FLIP_THRESHOLD_PX;
        isOpenRef.current = true;
        setAnchor(
          fitsBelow
            ? { left, top: rect.bottom + GAP_PX }
            : { left, bottom: window.innerHeight - rect.top + GAP_PX },
        );
      }, delayMs);
    },
    [delayMs],
  );

  const onPointerEnter = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      // Touch has no hover — a tap would otherwise leave a panel stuck open.
      if (event.pointerType === "touch") return;
      scheduleOpen(event.currentTarget);
    },
    [scheduleOpen],
  );

  // `pointerenter` alone is not enough: the product combo hydrates its real
  // editor ON hover, so the mounted-on-hover instance never receives the
  // enter event that caused it to exist and would stay mute until the pointer
  // left and came back. `pointermove` re-arms it; the open/pending guard makes
  // every subsequent move a two-ref check.
  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === "touch") return;
      scheduleOpen(event.currentTarget);
    },
    [scheduleOpen],
  );

  // The panel is position:fixed against coordinates captured at open time, so
  // any scroll invalidates it. Close rather than reposition — the pointer is
  // about to land somewhere else anyway.
  useEffect(() => {
    if (!anchor) return;
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [anchor, close]);

  useEffect(() => cancelPending, [cancelPending]);

  const hasContent =
    content !== null &&
    content !== undefined &&
    content !== false &&
    content !== "";

  const tooltip =
    anchor && hasContent && typeof document !== "undefined"
      ? createPortal(
          <div
            role="tooltip"
            data-testid="hover-tooltip"
            style={{
              position: "fixed",
              left: anchor.left,
              top: anchor.top,
              bottom: anchor.bottom,
              maxWidth: MAX_WIDTH_PX,
            }}
            className="pointer-events-none z-[70] rounded-md border bg-popover px-2 py-1 text-xs leading-snug text-popover-foreground shadow-md"
          >
            {content}
          </div>,
          document.body,
        )
      : null;

  return {
    triggerProps: {
      onPointerEnter,
      onPointerMove,
      onPointerLeave: close,
      onPointerDown: close,
    },
    tooltip,
  };
}
